import { Box, CircularProgress } from '@mui/material';
import {
  useContext,
  useEffect,
  useMemo, useRef, useState,
} from 'react';
import ImageSettings, { Size } from '../models/BlockComponentSettings/ImageSettings';
import BlockComponentImage from '../models/BlockComponentValues/BlockComponentImage';
import { ImageAspectRatio, ImageOrientation } from './Blocks/BlockImageList/ImageSettings';
import { useGetBase64Image } from '../hooks/UseGetBase64Image';
import { ImagesContext } from '../contexts/ImagesContext';
import { getFixedAspectRatioValue } from '../config';
import { BlockHeight } from '../models/BlockSettings';

interface CroppedImageProps {
  image: BlockComponentImage;
  settings: ImageSettings;
  objectFitStyle?: string;
  targetModalId?: string;
  blockHeight?: BlockHeight;
  isEditing?: boolean;
  useFixedAspectRatio?: boolean;
}

const CroppedImage = ({
  image,
  settings,
  blockHeight,
  objectFitStyle,
  targetModalId,
  isEditing,
  useFixedAspectRatio = false,
}: CroppedImageProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const targetModal = `image-showcase-${targetModalId}`;
  const base64Image = useGetBase64Image(image.value);
  const { isLoading: isFetching } = useContext(ImagesContext);
  const [selectedBorderRadius, setSelectedBorderRadius] = useState<number>(0);
  const [dimensions, setDimensions] = useState<Size>({ width: 0, height: 0 });
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const validObjectFitValues: Array<'contain' | 'cover' | 'fill' | 'none' | 'scale-down'> = ['contain', 'cover', 'fill', 'none', 'scale-down'];
  const isObjectFitValue = validObjectFitValues.includes(objectFitStyle as 'contain' | 'cover' | 'fill' | 'none' | 'scale-down');
  const objectFitValue: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down' = isObjectFitValue ? objectFitStyle as 'contain' | 'cover' | 'fill' | 'none' | 'scale-down' : 'contain';

  const percentPosition = useMemo(() => {
    const { x, y } = settings.position;
    const xPercent = (x / settings.frameSize.width) * 100;
    const yPercent = (y / settings.frameSize.height) * 100;
    return { x: xPercent, y: yPercent };
  }, [settings]);

  const handleShowModal = (element: EventTarget | null) => {
    if (isEditing) return;
    if (!image.settings.enableViewer) return;

    // get image data-unique-value from element
    const imageElement = element as HTMLImageElement;
    const elementUniqueValue = imageElement.getAttribute('data-unique-value');
    const modal = document.getElementById(targetModal);

    if (modal) {
      const containers = modal.getElementsByClassName('image-container');

      // iterate through containers to find the one with the same src
      for (let i = 0; i < containers.length; i += 1) {
        const container = containers[i] as HTMLElement;
        const imageUniqueValue = container.getElementsByTagName('img')[0].getAttribute('data-unique-value');

        if (imageUniqueValue === elementUniqueValue) {
          // set the container with the same src to display
          container.classList.add('show');
          container.style.display = 'block';
        } else {
          // hide other containers
          container.classList.remove('show');
          container.style.display = 'none';
        }
      }

      modal.classList.add('show');
      modal.style.display = 'block';
    }
  };

  const getBase64ImageDimensions = (base64Image: string) => {
    const img = new Image();
    img.src = base64Image;
    let size;
    img.decode().then(() => {
      setDimensions({ width: img.width, height: img.height } as Size);
      size = { width: img.width, height: img.height } as Size;
    }).catch((error) => {
      // console.log('img.decode() error', error);
      size = { width: 0, height: 0 } as Size;
    });

    if (!size) {
      size = { width: 0, height: 0 } as Size;
    }

    return size;
  };

  useEffect(() => {
    getBase64ImageDimensions(base64Image);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base64Image]);

  const handleBorderRadiusValue = (borderRadius: string) => {
    let borderRadiusValue = 0;

    if (settings.orientation === ImageOrientation.Circle) {
      borderRadiusValue = 500;
    } else if (!Number.isNaN(Number(borderRadius))) {
      borderRadiusValue = Number(borderRadius) / 3;
    }

    setSelectedBorderRadius(borderRadiusValue);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(isFetching);
    }, 1000);

    return () => clearTimeout(timer);
  }, [isFetching]);

  const aspectRatioSettings = useMemo(() => {
    let ratio;

    if (useFixedAspectRatio) ratio = getFixedAspectRatioValue(blockHeight);
    else if (settings.fixedAspect) {
      switch (settings.orientation) {
        case ImageOrientation.Square:
        case ImageOrientation.Circle:
          ratio = 1;
          break;
        case ImageOrientation.Landscape:
          switch (settings.aspectRatio) {
            case ImageAspectRatio.SixteenByNine:
              ratio = 9 / 16;
              break;
            case ImageAspectRatio.FourByThree:
            default:
              ratio = 3 / 4;
              break;
          }
          break;
        case ImageOrientation.Portrait:
          switch (settings.aspectRatio) {
            case ImageAspectRatio.SixteenByNine:
              ratio = 16 / 9;
              break;
            case ImageAspectRatio.FourByThree:
            default:
              ratio = 4 / 3;
              break;
          }
          break;
        default:
          ratio = 3 / 4;
          break;
      }
    } else {
      ratio = settings.frameSize.height / settings.frameSize.width;
    }

    if (settings.size && settings.size.borderRadius) handleBorderRadiusValue(settings.size.borderRadius);

    return {
      ratio: ratio * 100,
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.aspectRatio, settings.fixedAspect, settings.frameSize.height, settings.frameSize.width, settings.orientation, settings.size, dimensions]);

  return (
    <>
      {isLoading ? (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={10} />
        </Box>
      ) : (
        <Box
          ref={containerRef}
          sx={{
            width: '100%',
            height: 'auto',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: 'auto',
              position: 'relative',
              overflow: 'hidden',
              paddingBottom: `${aspectRatioSettings.ratio}%`,
              borderRadius: selectedBorderRadius,
            }}
          >
            <img
              alt={image.alt}
              src={base64Image}
              data-toggle="modal"
              data-target={`#${targetModal}`}
              data-enlarge={image.settings.enableViewer}
              data-unique-value={image.uniqueId}
              onClick={(e) => handleShowModal(e.target)}
              style={{
                position: 'absolute',
                objectFit: objectFitValue,
                top: '50%',
                left: '50%',
                transform: `translate(-50%, -50%) translate(${percentPosition.x}%, ${percentPosition.y}%) scale(${settings.scale})`,
                transition: 'width 0.3s, height 0.3s',
                width: '100%',
                height: '100%',
              }}
            />
          </Box>
        </Box>
      )}
    </>

  );
};

export default CroppedImage;
